<template>
    <validation-observer ref="form" v-slot="{ handleSubmit, meta }">
        <modal
            :id="modalId"
            v-model="modalShow"
            :title="!isCreate ? 'Edit Coupon' : 'Create Coupon'"
            scrollable
            ok-title="Save"
            size="lg"
            :ok-disabled="!meta.valid || loading"
            @ok.prevent="handleSubmit(_, save)"
            @hide="hideModal"
            @shown="setValues"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form @submit.prevent="handleSubmit(_, save)" v-if="values">
                <b-row>
                    <b-col>
                        <form-date-range-picker
                            label="Date range:"
                            :range-options="predefinedRanges"
                            :is-responsive="false"
                            :value="dateRange"
                            :date-format="userUnicodeDateFormat"
                            variant="default"
                            @input="handleDateRangeChange"
                        ></form-date-range-picker>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="name" rules="required" v-slot="{ errors, handleChange }">
                            <form-input
                                label="Coupon Name:"
                                v-model="values.name"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="description" rules="required" v-slot="{ errors, handleChange }">
                            <form-textarea
                                label="Coupon Description:"
                                v-model="values.description"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-textarea>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="type" rules="required" v-slot="{ errors, handleChange }">
                            <form-select
                                label="Coupon Type:"
                                v-model="values.type"
                                :error="errors[0]"
                                :options="typeOptions"
                                @input="handleChange"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="values.type === 'TRIAL_EXTENSION'">
                    <b-col>
                        <validation-provider name="period" rules="required" v-slot="{ errors, handleChange }">
                            <form-input
                                label="Trial Period Extend:"
                                v-model="values.period"
                                type="number"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-else>
                    <b-col>
                        <validation-provider name="discount" rules="required" v-slot="{ errors, handleChange }">
                            <form-input
                                label="Coupon Discount:"
                                v-model="values.discount"
                                type="number"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="package_status" rules="required" v-slot="{ errors, handleChange }">
                            <form-select
                                label="Coupon Status:"
                                v-model="values.package_status"
                                :error="errors[0]"
                                :options="statusOptions"
                                @change="handleChange"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
    import isEmpty from 'lodash/isEmpty';
    import parseISO from 'date-fns/parseISO';
    import format from 'date-fns/format';
    import { mapGetters } from 'vuex';
    import { formatResponseError } from '../../../utils/api';
    import Modal from '../../generic/modal/modal.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import {
        nameToRange,
        ALL_DATES,
        CUSTOM_RANGE,
        NEXT_MONTH,
        NEXT_THREE_MONTHS,
        NEXT_SIX_MONTHS,
        NEXT_YEAR,
        TODAY,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import FormDateRangePicker from '../../generic/form-date-range-picker/form-date-range-picker.vue';

    export default {
        name: 'SystemEmailEditModal',
        components: {
            Modal,
            BusyModal,
            FormInput,
            FormTextarea,
            FormSelect,
            FormDateRangePicker,
        },
        props: {
            modalId: String,
            isCreate: {
                type: Boolean,
                default: false,
            },
            update: {
                type: Function,
                default: () => Promise.resolve(),
            },
            create: {
                type: Function,
                default: () => Promise.resolve(),
            },
            initialValues: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                modalShow: false,
                loading: false,
                values: this.getInitialValues(),
                typeOptions: [
                    {
                        text: 'Trial Extension',
                        value: 'TRIAL_EXTENSION',
                    },
                    {
                        text: 'Fixed amount discount',
                        value: 'FLAT_DISCOUNT',
                    },
                    {
                        text: 'Percentage Discount',
                        value: 'PERCENT_DISCOUNT',
                    },
                ],
                statusOptions: [
                    {
                        text: 'Active',
                        value: 'ACTIVE',
                    },
                    {
                        text: 'Inactive',
                        value: 'INACTIVE',
                    },
                    {
                        text: 'Disabled',
                        value: 'DISABLED',
                    },
                ],
                dateRange: nameToRange(TODAY),
                predefinedRanges: [
                    ALL_DATES,
                    TODAY,
                    NEXT_MONTH,
                    NEXT_THREE_MONTHS,
                    NEXT_SIX_MONTHS,
                    NEXT_YEAR,
                    CUSTOM_RANGE,
                ],
                dateRequestFormat: 'yyyy-MM-dd',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
        },
        methods: {
            getInitialValues() {
                return {
                    id: this.initialValues.id || null,
                    name: this.initialValues.name || '',
                    description: this.initialValues.description || '',
                    type: this.initialValues.type || 'TRIAL_EXTENSION',
                    period: this.initialValues.period || 0,
                    date_from: this.initialValues.date_from || '',
                    date_to: this.initialValues.date_to || '',
                    package_status: this.initialValues.package_status || 'ACTIVE',
                    discount: this.initialValues.discount || 0,
                };
            },
            hideModal() {
                this.$bvModal.hide(this.modalId);
            },
            setValues() {
                this.$refs.form.setValues(this.values);
            },
            async save() {
                this.loading = true;

                try {
                    const payload = {
                        ...this.values,
                        date_from: format(this.dateRange.from, this.dateRequestFormat),
                        date_to: format(this.dateRange.to, this.dateRequestFormat),
                    };
                    if (this.isCreate) {
                        await this.create(payload);
                    } else {
                        await this.update(payload);
                    }
                    this.$emit('success-save');
                    this.$nextTick(() => {
                        this.hideModal();
                    });
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({ text: generic });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);
                    }
                } finally {
                    this.loading = false;
                }
            },
            handleDateRangeChange(value) {
                const { from, to } = value;
                this.values.date_from = from;
                this.values.date_to = to;
                this.dateRange = { ...value };
            },
        },
        watch: {
            initialValues() {
                this.values = this.getInitialValues();
                this.dateRange = {
                    name: 'custom range',
                    from: parseISO(this.values.date_from),
                    to: parseISO(this.values.date_to),
                };
            },
            modalShow() {
                this.values = this.getInitialValues();
            },
        },
    };
</script>

<style lang="scss"></style>
